import { useQuery } from "@apollo/client/react";
import { CircularProgress, Stack, Typography, styled } from "@mui/material";
import { DownloadRowsReportDocument } from "../../graphql/types";
import type { DownloadRowsReportQuery, DownloadRowsReportQueryVariables } from "../../graphql/types";
import { humanizeNumber } from "../../lib/Util";
import { GqlError } from "../common/GqlError";
import { DataLoader, ErrorContainer, LoadingContainer, SuccessContainer } from "../DataLoader";

const RowCountContainer = styled("div")({
  display: "flex",
  width: "184px",
  padding: "24px 34px",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

const SimpleRowCountContent = ({ title, count }: { title: string; count: number }) => {
  return (
    <RowCountContainer>
      <Typography variant="heading.three" sx={{ color: "text40.main" }}>
        {humanizeNumber(count)}
      </Typography>{" "}
      <Typography variant="heading.tag" sx={{ color: "text40.main" }}>
        {title}
      </Typography>{" "}
    </RowCountContainer>
  );
};

const DownloadsSummaryPanel = () => {
  const { data, loading, error } = useQuery<DownloadRowsReportQuery, DownloadRowsReportQueryVariables>(
    DownloadRowsReportDocument,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        dimensions: ["purpose"],
      },
    }
  );
  const rows = data?.downloadRowsReport?.rows ?? [];
  const backfillRowCount = rows.find((r) => r.purpose === "backfill")?.totalRowCount ?? 0;
  const productionRowCount = rows.find((r) => r.purpose === "production")?.totalRowCount ?? 0;
  const proofRowCount = rows.find((r) => r.purpose === "proof")?.totalRowCount ?? 0;
  return (
    <DataLoader loading={loading} error={error} success={Boolean(data)}>
      <LoadingContainer>
        <Stack direction="row" sx={{ justifyContent: "center", mt: "24px", alignItems: "center" }}>
          <CircularProgress size="24px" />
        </Stack>
      </LoadingContainer>
      <ErrorContainer>
        <Stack sx={{ mt: "16px" }}>
          <GqlError error={error} />
        </Stack>
      </ErrorContainer>
      <SuccessContainer>
        <Stack
          direction="row"
          sx={{
            marginTop: "26px",
            justifyContent: "space-between",
            overflowX: "auto",
          }}
        >
          <SimpleRowCountContent title="Production" count={productionRowCount} />
          <SimpleRowCountContent title="Backfill" count={backfillRowCount} />
          <SimpleRowCountContent title="Test" count={proofRowCount} />
          <SimpleRowCountContent title="Forecasted" count={data?.downloadRowsReport?.forecast || 0} />
          <SimpleRowCountContent title="Contracted" count={data?.downloadRowsReport?.contracted || 0} />
        </Stack>
        <Stack direction="row" sx={{ mt: "12px", justifyContent: "center" }}>
          <Typography variant="heading.tag" sx={{ color: "text40.main", textTransform: "uppercase" }}>
            row counts mtd
          </Typography>
        </Stack>
      </SuccessContainer>
    </DataLoader>
  );
};
export { DownloadsSummaryPanel };
